/**
 * Adapptio core library.
 *
 * @package core
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { TBlueprintIDTNode } from "@adapptio/lib-blueprint";
import { IEditorApiBlueprintRevision, TEditorApiBlueprintHistory } from "../api";
import { IService } from "../services";
import { IReleaseInfo, IScmInfo } from "./IResourceManager";

/**
 * Resource Adapter Types
 */
export enum RESOURCE_ADAPTER_TYPE {
	fs = "fs",
	git = "git"
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface IRawResourceErrorDetails {
	[K: string]: any;
}
[];

export interface IRawResource {
	uri: string;
	label: string;
	location: string;
	basePath: string;
	idt?: TBlueprintIDTNode;
	isValid: boolean;
	errors?: IRawResourceErrorDetails;
	dependencies?: string[];
	lastModified?: number;
}

/**
 * Resource Adapter Options
 */
export interface IResourceAdapterOptions {
	/** Path to blueprints */
	blueprintsPath: string;
}

export interface IResourceRevisionInfo
	extends Pick<IEditorApiBlueprintRevision, "modifiedBy" | "modifiedAt" | "revision"> {}

/**
 * Resource Adapter
 */
export interface IResourceAdapter extends IService {
	manifestUri: string;

	loadManifest(): Promise<IRawResource>;
	loadResourceList(): Promise<string[]>;
	loadResource(filepath: string): Promise<IRawResource>;
	loadResourceFromString(content: string, filename: string): Promise<IRawResource>;
	getLastRevision(path: string): Promise<IEditorApiBlueprintRevision>;

	/**
	 * Creates directory
	 *
	 * @param params
	 */
	createDir(params: { dirName: string; author?: string }): Promise<IRawResource>;

	/**
	 * Removes directory
	 *
	 * @param params
	 */
	removeDir(params: { dirName: string; author?: string }): Promise<void>;

	/**
	 * Renames directory
	 *
	 * @param params
	 */
	renameDir(params: { dirName: string; newName: string; basePath: string }): Promise<IRawResource>;

	/**
	 * Creates blueprint
	 *
	 * @param params Params
	 */
	createBlueprint(params: {
		content?: string;
		basePath: string;
		name: string;
		author?: string;
	}): Promise<IRawResource>;

	/**
	 * Updates blueprint content
	 *
	 * @param params Params
	 */
	updateBlueprintContent(params: {
		path: string;
		content: string;
		sourceRevision?: string;
		author?: string;
	}): Promise<IRawResource>;

	/**
	 * Updates manifest content
	 *
	 * @param params Params
	 */
	updateManifestContent(params: {
		content: string;
		sourceRevision?: string;
		author?: string;
	}): Promise<IRawResource>;

	/**
	 * Renames blueprint
	 *
	 * @param params Params
	 */
	renameBlueprint(params: {
		path: string;
		basePath: string;
		name: string;
		author?: string;
	}): Promise<IRawResource>;

	/**
	 * Removes blueprint
	 *
	 * @param params Params
	 */
	removeBlueprint(params: { path: string; author?: string }): Promise<void>;

	/**
	 * Returns blueprint history
	 *
	 * @param path Blueprint path
	 */
	getBlueprintHistory(params: { path: string }): Promise<TEditorApiBlueprintHistory>;

	/**
	 * Returns blueprint's revision
	 *
	 * @param param Params
	 */
	getBlueprintRevision(params: { commitSha: string; path: string }): Promise<string>;

	/**
	 * Returns blueprint content for specific revision
	 *
	 * @param param Params
	 */
	getBlueprintContent(params: { path: string }): Promise<string>;

	/**
	 * Returns manifest content
	 */
	getManifestContent(): Promise<string>;

	/**
	 * Adds an asset
	 *
	 * @param filePath Path to the asset file
	 * @param author Author of the asset
	 */
	addAsset(filePath: string, author: string): Promise<void>;

	/**
	 * Creates a release
	 *
	 * @param tagName Name of the release tag
	 * @param releaseNotes Notes for the release
	 */
	makeRelease(tagName: string, releaseNotes: string, author: string): Promise<string>;

	/**
	 * Returns a list of releases
	 */
	getReleaseList(): Promise<IReleaseInfo[]>;

	/**
	 * Returns next release tag name
	 */
	getNextReleaseTagName(): Promise<string>;

	/**
	 * Synchronize repository
	 *
	 * @param overwrite Overwrite local changes
	 */
	syncRepository(params: { overwrite?: boolean; author?: string }): Promise<void>;

	/**
	 * Returns resource URI from path
	 *
	 * @param name Resource name
	 * @param basePath Base path
	 */
	getResourceUriFromPath(name: string, basePath?: string): string;

	/**
	 * Returns directory URI from path
	 *
	 * @param name Directory name
	 */
	getDirectoryUriFromPath(name: string): string;

	/**
	 * Returns SCM information
	 */
	getScmInfo(): Promise<IScmInfo|null>;
}
